import React, { useEffect, useState, useCallback } from 'react';
import { createPortal } from 'react-dom';
import Swal from 'sweetalert2';

// ==============================
// API
// ==============================
const API_BASE = 'https://api.cleany.qa/api/v1/admin';

async function getContacts() {
  const res = await fetch(`${API_BASE}/contactuses`);
  const json = await res.json();
  const list = json.data ?? json.list ?? [];
  return { list };
}

async function createContact(data: Record<string, unknown>) {
  const res = await fetch(`${API_BASE}/contactuses`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data),
  });
  if (!res.ok) throw new Error('Create failed');
  return res.json();
}

async function updateContact(id: number, data: Record<string, unknown>) {
  const res = await fetch(`${API_BASE}/contactuses/${id}`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data),
  });
  if (!res.ok) throw new Error('Update failed');
  return res.json();
}

async function deleteContact(id: number) {
  const res = await fetch(`${API_BASE}/contactuses/${id}`, {
    method: 'DELETE',
  });
  if (!res.ok) throw new Error('Delete failed');
  return res.json();
}

// ==============================
// SweetAlert2 helpers
// ==============================
const showSuccess = (msg: string) =>
  Swal.fire({
    icon: 'success',
    title: msg,
    timer: 2000,
    showConfirmButton: false,
    toast: true,
    position: 'top-end',
    background: '#1a1a2e',
    color: '#fff',
    iconColor: '#4ade80',
  });

const showError = (msg: string) =>
  Swal.fire({
    icon: 'error',
    title: 'Oops!',
    text: msg,
    toast: true,
    position: 'top-end',
    timer: 3000,
    showConfirmButton: false,
    background: '#1a1a2e',
    color: '#fff',
    iconColor: '#f87171',
  });

const confirmDelete = async (msg: string): Promise<boolean> => {
  const result = await Swal.fire({
    title: 'Are you sure?',
    text: msg,
    icon: 'warning',
    showCancelButton: true,
    confirmButtonText: 'Yes, delete it!',
    cancelButtonText: 'Cancel',
    background: '#1a1a2e',
    color: '#fff',
    iconColor: '#facc15',
    confirmButtonColor: '#ef4444',
    cancelButtonColor: '#6b7280',
  });
  return result.isConfirmed;
};

// ==============================
// Types
// ==============================
type ContactType = 'email' | 'phone' | 'whatsapp';

type Contact = {
  id: number;
  key: ContactType;
  value: string;
};

type ContactFormState = {
  key: ContactType;
  value: string;
};

const emptyForm: ContactFormState = {
  key: 'email',
  value: '',
};

// ---- icon + label map ----
const TYPE_META: Record<
  ContactType,
  { label: string; icon: string; color: string; placeholder: string }
> = {
  email: {
    label: 'Email',
    icon: '✉️',
    color: '#60a5fa',
    placeholder: 'example@email.com',
  },
  phone: {
    label: 'Phone',
    icon: '📞',
    color: '#34d399',
    placeholder: '+201234567890',
  },
  whatsapp: {
    label: 'WhatsApp',
    icon: '💬',
    color: '#4ade80',
    placeholder: 'https://wa.me/201234567890',
  },
};

// ==============================
// Component
// ==============================
const ContactUs: React.FC = () => {
  const [contacts, setContacts] = useState<Contact[]>([]);
  const [loading, setLoading] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [formState, setFormState] = useState<ContactFormState>(emptyForm);
  const [errors, setErrors] = useState<{ [key: string]: string }>({});

  // ---- fetch ----
  const fetchContacts = useCallback(async () => {
    setLoading(true);
    try {
      const { list } = await getContacts();
      setContacts(list as Contact[]);
    } catch {
      showError('Failed to fetch contacts');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchContacts();
  }, [fetchContacts]);

  // ---- handlers ----
  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>,
  ) => {
    const { name, value } = e.target;
    setFormState((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) setErrors((prev) => ({ ...prev, [name]: '' }));
  };

  const validate = () => {
    const newErrors: { [key: string]: string } = {};
    if (!formState.value.trim()) newErrors.value = 'Value is required';
    if (
      formState.key === 'email' &&
      formState.value &&
      !formState.value.includes('@')
    )
      newErrors.value = 'Enter a valid email';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!validate()) return;

    const payload: Record<string, unknown> = {
      key: formState.key,
      value: formState.value,
    };

    try {
      if (editingId === null) {
        await createContact(payload);
        showSuccess('Contact Created');
      } else {
        await updateContact(editingId, payload);
        showSuccess('Contact Updated');
      }
      setIsModalOpen(false);
      fetchContacts();
    } catch {
      showError('Submission Failed');
    }
  };

  const handleDelete = async (contact: Contact) => {
    const meta = TYPE_META[contact.key] ?? { label: contact.key };
    const confirmed = await confirmDelete(
      `Delete ${meta.label}: "${contact.value}"?`,
    );
    if (confirmed) {
      try {
        await deleteContact(contact.id);
        showSuccess('Deleted successfully');
        fetchContacts();
      } catch {
        showError('Deletion failed');
      }
    }
  };

  const openEdit = (contact: Contact) => {
    setEditingId(contact.id);
    setFormState({ key: contact.key, value: contact.value });
    setErrors({});
    setIsModalOpen(true);
  };

  const openCreate = () => {
    setEditingId(null);
    setFormState(emptyForm);
    setErrors({});
    setIsModalOpen(true);
  };

  const currentMeta = TYPE_META[formState.key];

  // ==============================
  // Render
  // ==============================
  return (
    <section className="cyber-section-card">
      {/* Header */}
      <div className="crud-page-header">
        <div>
          <p className="cyber-page-kicker">Settings</p>
          <h1 className="cyber-standalone-title">Contact Us</h1>
        </div>
        <button className="crud-add-button" onClick={openCreate}>
          + Add Contact
        </button>
      </div>

      {/* Cards Grid */}
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
          gap: '16px',
          marginTop: '16px',
        }}
      >
        {loading ? (
          <div className="crud-loading">Syncing...</div>
        ) : contacts.length === 0 ? (
          <div
            style={{
              gridColumn: '1 / -1',
              textAlign: 'center',
              padding: '48px',
              opacity: 0.4,
            }}
          >
            No contact info found. Add one!
          </div>
        ) : (
          contacts.map((contact) => {
            const meta = TYPE_META[contact.key] ?? {
              label: contact.key,
              icon: '📌',
              color: '#94a3b8',
              placeholder: '',
            };
            return (
              <div
                key={contact.id}
                style={{
                  background: 'var(--color-surface, #1e1e2e)',
                  border: `1px solid ${meta.color}33`,
                  borderLeft: `4px solid ${meta.color}`,
                  borderRadius: '10px',
                  padding: '18px 20px',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '10px',
                  boxShadow: `0 4px 20px ${meta.color}11`,
                  transition: 'box-shadow 0.2s',
                }}
              >
                {/* Type badge */}
                <div
                  style={{ display: 'flex', alignItems: 'center', gap: '8px' }}
                >
                  <span style={{ fontSize: '20px' }}>{meta.icon}</span>
                  <span
                    style={{
                      fontSize: '12px',
                      fontWeight: 700,
                      letterSpacing: '0.08em',
                      textTransform: 'uppercase',
                      color: meta.color,
                      background: `${meta.color}18`,
                      padding: '2px 10px',
                      borderRadius: '20px',
                    }}
                  >
                    {meta.label}
                  </span>
                </div>

                {/* Value */}
                <div
                  style={{
                    fontSize: '14px',
                    color: 'var(--color-text, #e2e8f0)',
                    wordBreak: 'break-all',
                    fontFamily: 'monospace',
                  }}
                >
                  {contact.value.startsWith('http') ? (
                    <a
                      href={contact.value}
                      target="_blank"
                      rel="noreferrer"
                      style={{ color: meta.color, textDecoration: 'none' }}
                    >
                      {contact.value}
                    </a>
                  ) : (
                    contact.value
                  )}
                </div>

                {/* Actions */}
                <div
                  className="crud-actions"
                  style={{ marginTop: 'auto', paddingTop: '8px' }}
                >
                  <button
                    className="crud-action-button"
                    onClick={() => openEdit(contact)}
                  >
                    Edit
                  </button>
                  <button
                    className="crud-action-button crud-action-danger"
                    onClick={() => handleDelete(contact)}
                  >
                    Delete
                  </button>
                </div>
              </div>
            );
          })
        )}
      </div>

      {/* Modal */}
      {isModalOpen &&
        createPortal(
          <div
            className="crud-modal-overlay"
            onMouseDown={(e) =>
              e.target === e.currentTarget && setIsModalOpen(false)
            }
          >
            <div className="crud-modal-panel">
              <div className="crud-modal-head">
                <h2>{editingId ? 'Edit' : 'Add'} Contact</h2>
                <button
                  className="crud-modal-close"
                  onClick={() => setIsModalOpen(false)}
                >
                  X
                </button>
              </div>

              <form className="crud-form" onSubmit={handleSubmit}>
                {/* Type */}
                <label className="crud-field">
                  <span>Contact Type</span>
                  <select
                    name="key"
                    value={formState.key}
                    onChange={handleInputChange}
                  >
                    {(Object.keys(TYPE_META) as ContactType[]).map((t) => (
                      <option key={t} value={t}>
                        {TYPE_META[t].icon} {TYPE_META[t].label}
                      </option>
                    ))}
                  </select>
                </label>

                {/* Value */}
                <label className="crud-field">
                  <span>
                    {currentMeta.icon} {currentMeta.label} Value
                  </span>
                  <input
                    name="value"
                    value={formState.value}
                    onChange={handleInputChange}
                    placeholder={currentMeta.placeholder}
                    className={errors.value ? 'error' : ''}
                    style={{
                      borderColor: errors.value ? '#ef4444' : undefined,
                    }}
                  />
                  {errors.value && (
                    <span className="field-error">{errors.value}</span>
                  )}
                </label>

                {/* Preview */}
                {formState.value && (
                  <div
                    style={{
                      padding: '10px 14px',
                      borderRadius: '8px',
                      background: `${currentMeta.color}12`,
                      border: `1px solid ${currentMeta.color}33`,
                      fontSize: '13px',
                      color: currentMeta.color,
                      fontFamily: 'monospace',
                      wordBreak: 'break-all',
                    }}
                  >
                    {currentMeta.icon} Preview: {formState.value}
                  </div>
                )}

                <div className="crud-modal-actions">
                  <button
                    type="button"
                    className="crud-action-button"
                    onClick={() => setIsModalOpen(false)}
                  >
                    Cancel
                  </button>
                  <button type="submit" className="crud-add-button">
                    {editingId ? 'Save Changes' : 'Add Contact'}
                  </button>
                </div>
              </form>
            </div>
          </div>,
          document.body,
        )}
    </section>
  );
};

export default ContactUs;
